import { msg } from '@lingui/core/macro';
import { Form, Formik, FormikValues } from 'formik';
import _ from 'lodash';
import { Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import { getBoardTemplateVisibility } from 'modules/template-editor/domain/utils/board-template-visibility';
import { useGetOwnerBoardTemplatesQuery } from 'modules/template-editor/store/board-templates-api';
import { locationsToOptions } from 'modules/locations/domain/data-mappers/locations-to-options';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { removeChampionshipFromList } from 'modules/organizer/store/organizer';
import { fetchOrganizer } from 'modules/organizer/store/organizer/actions';
import { trackEvent } from 'shared/analytics';
import { ChampionshipStateData } from 'modules/championship/store/championship';
import { deleteChampionship as deleteChampionshipAction, upsertChampionship } from 'modules/championship/store/championship/actions';
import { championshipDataSelector, championshipIsDeletingSelector } from 'modules/championship/store/championship/selectors';
import React, { useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePreventNavigation } from 'shared/hooks';
import { useFetchAvailableFeature } from 'shared/hooks/fetchering/use-fetch-available-feature';
import { isDuplicateRequest } from 'shared/errors';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { Options } from 'shared/types/option';
import { TeamPlayersNumber } from 'shared/types/team-players-number';
import { getTeamPlayersNumberOptions } from 'shared/utils';
import { AppDispatch } from 'store/store';
import UDFormDateRange from 'ud-form/components/datetime/range';
import UDFormInput from 'ud-form/components/input';
import UDFormTextarea from 'ud-form/components/input/textarea';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import LocationSelect from 'modules/locations/ui/components/location-select';
import UdFormTemplateSelect from 'ud-form/components/select/ud-form-template-select';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

/** «5x5» — формат, а не слово: переводить нечего, поэтому подпись собирается без каталога. */
const teamPlayersNumberToOption = (playersNumber: number) => ({
  label: `${playersNumber}x${playersNumber}`,
  value: playersNumber,
});

interface ChampionshipInfoFormProps {
  tournamentId: number;
  championshipId?: number;
  initialValues?: {
    id?: number;
    name: string;
    startDate: Date;
    endDate: Date;
  };
  onCreateGameModalOpen?: () => void;
}

export const ChampionshipInfoForm = ({ tournamentId, championshipId }: ChampionshipInfoFormProps) => {
  const location = useLocation();
  const state = location.state as undefined | { highlight: string[] };
  const isCreate = useMemo(() => {
    return location.pathname.includes('create');
  }, [location.pathname]);

  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const dispatch = useDispatch<AppDispatch>();
  const championship: ChampionshipStateData | undefined = useSelector(championshipDataSelector(championshipId ?? -1));
  const navigate = useNavigate();

  if (!championship?.id && !isCreate) {
    navigate(Pages.CHAMPIONSHIPS.INDEX);
  }

  const isDelete = useSelector(championshipIsDeletingSelector);

  const hasChampionship = !!championship;
  const typeOptions = getTeamPlayersNumberOptions().map(teamPlayersNumberToOption);
  const teamPlayersNumber = championship?.settings?.teamPlayersNumber || TeamPlayersNumber.FIVE;

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const { data, isFetching: isFeatureFetching } = useFetchAvailableFeature();
  const { OBS_INTEGRATION = false } = data ?? {};
  const isAdmin = useIsAdmin();

  // Тот же кэш, что у редактора табло: открытый раньше список шаблонов здесь не грузится заново.
  const { data: templates, isFetching: isTemplatesFetching } = useGetOwnerBoardTemplatesQuery(
    { ownerId: tournamentId, visibility: getBoardTemplateVisibility(isAdmin) },
    { skip: !tournamentId },
  );

  const templatesOptions: Options<Template['id']> = useMemo(() => {
    if (isTemplatesFetching) return [];
    if (!templates?.length) return [];

    return templates.map((template) => ({
      label: template.name === 'ImGame (стандарт)' ? 'ImGame' : template.name,
      value: template.id,
    }));
  }, [isTemplatesFetching, templates]);

  const locationsOptions = useMemo(() => {
    return locationsToOptions(championship?.locations ?? []);
  }, [championship?.locations]);

  // Мемо, а не литерал на каждый рендер: у формы `enableReinitialize`, и новый объект после `setIsFormDirty` в сабмите
  // сбрасывал её посреди запроса — снимал замок «Сохранить» и откатывал поля к прежним (IMGM-1561).
  const initialValues = useMemo(
    () => ({
      name: championship?.name || '',
      description: championship?.description || '',
      startDate: championship?.startDate ? new Date(championship.startDate) : '',
      endDate: championship?.endDate ? new Date(championship.endDate) : '',
      tournamentType: teamPlayersNumberToOption(teamPlayersNumber),
      locations: locationsOptions,
      template: _.find(templatesOptions, {
        value: championship?.settings?.overlayId,
      }),
    }),
    [
      championship?.name,
      championship?.description,
      championship?.startDate,
      championship?.endDate,
      championship?.settings?.overlayId,
      teamPlayersNumber,
      locationsOptions,
      templatesOptions,
    ],
  );

  // Промис отвечает, сохранён ли турнир: по нему правка решает, принять ли значения за новую базу формы.
  const handleSubmit = useCallback(
    (values: FormikValues): Promise<boolean> => {
      const { locations, tournamentType, template, ...otherValues } = values;
      const locationIds = locations ? locations.map((loc: SelectOption<Location>) => loc.value) : [];
      if (state && state.highlight) {
        state.highlight = [];
      }

      const data = {
        ...otherValues,
        name: otherValues.name.trim(),
        description: otherValues.description.trim(),
        id: championship?.id,
        tournamentId,
        locationIds,
        teamPlayersNumber: tournamentType.value,
        overlayId: OBS_INTEGRATION
          ? (template?.value ?? templates?.[0]?.id ?? null)
          : (_.find(templates, { access: 'default' })?.id ?? null),
      };

      // Раньше `unwrapResult` в `then` бросал на провале — unhandled rejection без тоста.
      return dispatch(upsertChampionship(data))
        .unwrap()
        .then((championshipId) => {
          dispatch(fetchOrganizer({ tournamentId }));

          if (!hasChampionship) {
            StepperMainTool.setByPath('stepper:main:createTournament', true);

            // Веха воронки «создай турнир». Только в этой ветке: правка существующего турнира
            // приходит в тот же `handleSubmit`, но созданием не является.
            trackEvent('Championship Created', {
              championship_id: championshipId,
              team_players_number: data.teamPlayersNumber,
              locations_count: locationIds.length,
              has_overlay_template: data.overlayId !== null,
              has_description: data.description.length > 0,
            });

            const pageUrl = Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules');

            navigate(pageUrl, { replace: true });
          }
          return true;
        })
        .catch((error) => {
          toastApiError(error, hasChampionship ? msg`Не удалось сохранить турнир` : msg`Не удалось создать турнир`);
          // Код 15 — повтор: первый запрос уже сохранил, правки не теряются.
          return isDuplicateRequest(error);
        });
    },
    [OBS_INTEGRATION, championship?.id, dispatch, hasChampionship, navigate, state, templates, toastApiError, tournamentId],
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);

  const deleteChampionship = useCallback(async () => {
    if (!championship?.id) {
      toast.warn(t`Турнир не найден`);
      return;
    }

    try {
      await dispatch(deleteChampionshipAction({ championshipId: championship.id })).unwrap();
    } catch (error) {
      toastApiError(error, msg`Не удалось удалить турнир`);
      return;
    }

    toast.success(t`Турнир удален`);
    closeDeleteModal();
    dispatch(removeChampionshipFromList({ id: championship.id }));
    navigate(Pages.CHAMPIONSHIPS.INDEX);
  }, [championship?.id, closeDeleteModal, dispatch, navigate, t, toastApiError]);

  const championshipName = championship?.name;

  const { PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.CHAMPIONSHIPS.INDEX,
  });

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={async (values, formikHelpers) => {
          if (isCreate) {
            // Текущие значения — база сразу: уход на страницу нового турнира не должен ловить «несохранённые
            // правки». `isSubmitting: true` переживает сброс — кнопку держит промис запроса, Formik снимет флаг сам.
            formikHelpers.resetForm({ values, isSubmitting: true });
            setIsFormDirty(false);
            return handleSubmit(values);
          }
          // Правка: база — только после успеха. Сброс до запроса делал форму «чистой» и на провале — правки
          // терялись при уходе без «Покинуть?».
          const isSaved = await handleSubmit(values);
          if (isSaved) {
            formikHelpers.resetForm({ values });
            setIsFormDirty(false);
          }
        }}
        validate={(values) => {
          const dirty = !_.isEqual(values, initialValues);
          if (dirty !== isFormDirty && !leavingConfirmed) {
            setIsFormDirty(dirty);
          }
          return {};
        }}
      >
        {(formProps) => {
          return (
            <Form onSubmit={formProps.handleSubmit}>
              <div className="mb-5 w-full gap-6 px-6">
                <div className="w-1/2">
                  <UDFormInput
                    autoFocus={isCreate}
                    name="name"
                    label={t`Название турнира`}
                    placeholder={t`Введите название`}
                    containerProps={{ className: 'mb-5' }}
                  />
                  <UDFormDateRange
                    autoComplete="off"
                    startName="startDate"
                    endName="endDate"
                    startLabel={t`Начало турнира`}
                    endLabel={t`Окончание турнира`}
                    containerProps={{ className: 'mb-5' }}
                  />
                  <UDFormSelect
                    name="tournamentType"
                    label={t`Тип турнира`}
                    placeholder={t`Выберите тип турнира`}
                    containerProps={{ className: 'mb-5' }}
                    options={typeOptions}
                  />
                  <LocationSelect
                    name="locations"
                    label={t`Места проведения`}
                    placeholder={t`Выберите место`}
                    containerProps={{ className: 'mb-5' }}
                    options={locationsOptions}
                    isMulti
                  />
                  <UdFormTemplateSelect
                    name="template"
                    label={t`Шаблоны`}
                    placeholder={t`Выберите шаблон`}
                    containerProps={{ className: 'mb-5' }}
                    options={templatesOptions}
                    isAvailable={OBS_INTEGRATION}
                    subscribeTo={Pages.SETTINGS.builders.subscription('OBS_INTEGRATION')}
                    highlight={state?.highlight?.includes('template')}
                    isLoading={isFeatureFetching}
                  />
                  <UDFormTextarea
                    name="description"
                    label={t`Описание`}
                    placeholder={t`Введите текст`}
                    containerProps={{ className: 'mb-5' }}
                  />

                  <div className="flex gap-6">
                    <UDButton
                      variant="primary"
                      type="submit"
                      disabled={!formProps.dirty || isTemplatesFetching || isDelete || !formProps.isValid}
                      className="w-full"
                    >
                      {t`Сохранить`}
                    </UDButton>

                    {!isCreate && (
                      <UDButton
                        variant="secondary"
                        type="button"
                        // Гаснет, пока сохраняется турнир: запрос идёт не отсюда, а из сабмита формы.
                        disabled={isDelete || isTemplatesFetching || formProps.isSubmitting}
                        className="w-full"
                        onClick={openDeleteModal}
                      >
                        {t`Удалить`}
                      </UDButton>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      <Modal.Confirm
        show={isDeleteModalOpen}
        title={championshipName ? t`Удалить турнир «${championshipName}»?` : t`Удалить турнир?`}
        description={t`Это действие необратимо.`}
        onConfirm={deleteChampionship}
        onCancel={closeDeleteModal}
      />

      <PreventNavigationModal />
    </>
  );
};
